import {
  Badge,
  Box,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  REDIS_STATUS_COLORS,
  REDIS_STATUS_LABELS,
  RedisSolicitacaoFilters,
  RedisSolicitacaoListResponse,
} from "@/models/auction-redis.model";
import { getAuctionRedisSolicitacoes } from "@/services/auctionRedis.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
} from "../FinancialShared/tableStyles";
import CreditAuctionProposalsPagination from "./credit-auction-proposals-pagination";
import { formatDate, formatDateTime, formatRate, toTitleCase } from "./formatters";

const headerStyles = { ...tableHeaderStyles, fontSize: "10px" };
const cellStyles = { ...tableCellStyles, fontSize: "12px" };

interface CreditAuctionProgressContentProps {
  appliedFilters: RedisSolicitacaoFilters;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/**
 * Body da aba "Progresso leilão": só tabela + paginação. Os filtros e o
 * título ficam no orquestrador (CreditAuctionProposalsPage) — segue o
 * padrão do CorbansTable.
 */
export default function CreditAuctionProgressContent({
  appliedFilters,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: CreditAuctionProgressContentProps) {
  const backgroundStyle = useConditionalBackground();

  const { data, isLoading } = useQuery<RedisSolicitacaoListResponse>({
    queryKey: [
      "auction-redis-solicitacoes",
      page,
      pageSize,
      appliedFilters.searchTerm,
      appliedFilters.statuses.join(","),
      appliedFilters.codigoLote,
    ],
    queryFn: () =>
      getAuctionRedisSolicitacoes({
        page,
        pageSize,
        searchTerm: appliedFilters.searchTerm || undefined,
        statuses: appliedFilters.statuses.length
          ? appliedFilters.statuses
          : undefined,
        codigoLote: appliedFilters.codigoLote || undefined,
      }),
    refetchOnWindowFocus: false,
  });

  const solicitacoes = data?.data ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = data?.total_paginas ?? 1;
  const currentPage = data?.pagina_atual ?? page;

  return (
    <Box bg="white" borderRadius="xl" boxShadow="sm" px={0} py={{ base: 2, md: 4 }}>
      <CreditAuctionProposalsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minH="260px">
          <Spinner size="xl" color="gray.500" />
        </Flex>
      ) : solicitacoes.length === 0 ? (
        <Flex justifyContent="center" alignItems="center" minH="260px">
          <Text color="gray.500" fontFamily="Lexend Deca">
            Nenhuma solicitação em andamento para os filtros informados.
          </Text>
        </Flex>
      ) : (
        <TableContainer borderRadius="lg" overflowX="auto">
          <Table variant="simple">
            <Thead {...backgroundStyle}>
              <Tr>
                <Th {...headerStyles} px={EDGE_COLUMN_PX} width="110px">
                  Nº SOLICITAÇÃO
                </Th>
                <Th {...headerStyles} width="120px">
                  CPF
                </Th>
                <Th {...headerStyles} width="90px">
                  STATUS
                </Th>
                <Th {...headerStyles} width="160px">
                  NOME
                </Th>
                <Th {...headerStyles} width="100px">
                  NASCIMENTO
                </Th>
                <Th {...headerStyles} width="100px">
                  ADMISSÃO
                </Th>
                <Th {...headerStyles} width="110px">
                  VALOR SOLICITADO
                </Th>
                <Th {...headerStyles} width="110px">
                  VALOR LIBERADO
                </Th>
                <Th {...headerStyles} width="70px">
                  PARCELAS
                </Th>
                <Th {...headerStyles} width="100px">
                  MARGEM
                </Th>
                <Th {...headerStyles} width="90px">
                  LANCE
                </Th>
                <Th {...headerStyles} px={EDGE_COLUMN_PX} width="120px">
                  CRIAÇÃO
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {solicitacoes.map((item) => (
                <Tr
                  key={`${item.codigoLote}-${item.numeroSolicitacao}`}
                  _odd={{ bg: "blackAlpha.50" }}
                >
                  <Td {...cellStyles} px={EDGE_COLUMN_PX} color="blue.500">
                    {item.numeroSolicitacao}
                    <Text fontSize="10px" color="gray.500">
                      {item.codigoLote}
                    </Text>
                  </Td>
                  <Td {...cellStyles}>{documentFormatter(item.cpf)}</Td>
                  <Td {...cellStyles}>
                    <Badge
                      colorScheme={REDIS_STATUS_COLORS[item.status]}
                      borderRadius="md"
                      px={2}
                      py={1}
                      fontFamily="Lexend Deca"
                      fontSize="10px"
                      fontWeight="normal"
                    >
                      {REDIS_STATUS_LABELS[item.status]}
                    </Badge>
                  </Td>
                  <Td {...cellStyles}>{toTitleCase(item.nomeTrabalhador)}</Td>
                  <Td {...cellStyles}>{formatDate(item.dataNascimento)}</Td>
                  <Td {...cellStyles}>{formatDate(item.dataAdmissao)}</Td>
                  <Td {...cellStyles}>
                    {BrlCurrencyFormatter(item.valorLiberado)}
                  </Td>
                  <Td {...cellStyles}>
                    {item.lance
                      ? BrlCurrencyFormatter(item.lance.valorLiberado)
                      : "-"}
                  </Td>
                  <Td {...cellStyles}>{item.nroParcelas}</Td>
                  <Td {...cellStyles}>
                    {BrlCurrencyFormatter(item.margemDisponivel)}
                  </Td>
                  <Td {...cellStyles}>
                    {item.lance ? (
                      <>
                        <Text fontSize="12px">pos. {item.lance.posicao}</Text>
                        <Text fontSize="12px">
                          {formatRate(item.lance.taxaDeJurosMensal)}
                        </Text>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td {...cellStyles} px={EDGE_COLUMN_PX}>
                    {formatDateTime(item.criadoEm)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <CreditAuctionProposalsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
}
